import { openDocumentPrintWindow } from "./pdfExportUtils";

export function exportOpportunityToPdf(formData) {
  const formattedValue = formData.estimatedValue 
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(formData.estimatedValue)
    : 'N/A';

  const hasGeneralInfo = formData.solicitationNumber || formData.shortDesc || (formData.agencies || []).filter(Boolean).length > 0 || (formData.locations || []).filter(Boolean).length > 0 || formData.naicsCode || formData.naicsDesc || formData.incumbent || formData.incumbentUrl || formData.currentStage;
  const hasValueDates = formData.estimatedValue || formData.contractDuration || formData.preSolicitationListDate || formData.solicitationDate || formData.dueDate || formData.awardDate;

  const styles = `
    body {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      color: #2c3e50;
      margin: 0;
      padding: 0;
      line-height: 1.6;
      background-color: #f8fafc;
    }
    .content-wrapper {
      background-color: #ffffff;
      max-width: 900px;
      margin: 30px auto;
      padding: 40px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      border-radius: 8px;
    }
          .header {
            border-bottom: 2px solid #34495e;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .title {
            font-size: 26px;
            font-weight: 700;
            color: #2c3e50;
            margin: 0 0 10px 0;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 13px;
            font-weight: 700;
            background-color: #e8f8f5;
            color: #1abc9c;
            text-transform: uppercase;
          }
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
          }
          .grid-col {
            background-color: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 15px;
          }
          .section-title {
            font-size: 14px;
            font-weight: 700;
            color: #7f8c8d;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 10px;
            border-bottom: 1px solid #dee2e6;
            padding-bottom: 5px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 14px;
            margin-bottom: 8px;
          }
          .info-label {
            color: #7f8c8d;
            font-weight: 500;
          }
          .info-value {
            font-weight: 600;
            color: #2c3e50;
            text-align: right;
          }
          .text-block {
            margin-bottom: 25px;
          }
          .text-block-title {
            font-size: 15px;
            font-weight: 700;
            color: #34495e;
            margin-bottom: 8px;
            border-bottom: 1px solid #dee2e6;
            padding-bottom: 5px;
          }
          .text-block-content {
            font-size: 14px;
            color: #2c3e50;
            white-space: pre-wrap;
          }
          .signature-section {
            margin-top: 50px;
            border-top: 2px dashed #bdc3c7;
            padding-top: 30px;
          }
          .signature-title {
            font-size: 16px;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 20px;
          }
          .signature-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }
          .signature-box {
            border-top: 1px solid #bdc3c7;
            padding-top: 10px;
            margin-top: 40px;
            font-size: 13px;
            color: #7f8c8d;
          }
      @media print {
        body {
          background-color: #ffffff;
          padding: 0;
        }
        .content-wrapper {
          box-shadow: none;
          margin: 0;
          padding: 15px;
          border-radius: 0;
          max-width: 100%;
        }
      }
    `;

    const bodyHtml = `
      <div class="content-wrapper">
        <div class="header">
          <h1 class="title">${formData.name || 'Unnamed Opportunity'}</h1>
          <div class="status-badge">${formData.status}</div>
        </div>
        
        <div class="grid">
          ${hasGeneralInfo ? `
          <div class="grid-col">
            <div class="section-title">General Info</div>
            ${formData.solicitationNumber ? `
            <div class="info-row">
              <span class="info-label">Solicitation Number:</span>
              <span class="info-value">${formData.solicitationNumber}</span>
            </div>
            ` : ''}
            ${formData.shortDesc ? `
            <div class="info-row">
              <span class="info-label">Short Description:</span>
              <span class="info-value">${formData.shortDesc}</span>
            </div>
            ` : ''}
            ${(formData.agencies || []).filter(Boolean).length > 0 ? `
            <div class="info-row">
              <span class="info-label">Agency:</span>
              <span class="info-value">${(formData.agencies || []).filter(Boolean).join(', ')}</span>
            </div>
            ` : ''}
            ${(formData.locations || []).filter(Boolean).length > 0 ? `
            <div class="info-row">
              <span class="info-label">Location of Performance:</span>
              <span class="info-value">${(formData.locations || []).filter(Boolean).join(', ')}</span>
            </div>
            ` : ''}
            ${formData.naicsCode ? `
            <div class="info-row">
              <span class="info-label">NAICS Code:</span>
              <span class="info-value">${formData.naicsCode}</span>
            </div>
            ` : ''}
            ${formData.naicsDesc ? `
            <div class="info-row">
              <span class="info-label">NAICS Description:</span>
              <span class="info-value">${formData.naicsDesc}</span>
            </div>
            ` : ''}
            ${formData.incumbent ? `
            <div class="info-row">
              <span class="info-label">Incumbent:</span>
              <span class="info-value">${formData.incumbent}</span>
            </div>
            ` : ''}
            ${formData.incumbentUrl ? `
            <div class="info-row">
              <span class="info-label">Incumbent URL:</span>
              <span class="info-value"><a href="${formData.incumbentUrl.startsWith('http') ? formData.incumbentUrl : 'https://' + formData.incumbentUrl}" target="_blank" style="color: #3498db; text-decoration: none;">${formData.incumbentUrl}</a></span>
            </div>
            ` : ''}
            ${formData.currentStage ? `
            <div class="info-row">
              <span class="info-label">Current Stage:</span>
              <span class="info-value">${formData.currentStage}</span>
            </div>
            ` : ''}
          </div>
          ` : ''}
          
          ${hasValueDates ? `
          <div class="grid-col">
            <div class="section-title">Value & Dates</div>
            ${formData.estimatedValue ? `
            <div class="info-row">
              <span class="info-label">Estimated Value:</span>
              <span class="info-value">${formattedValue}</span>
            </div>
            ` : ''}
            ${formData.contractDuration ? `
            <div class="info-row">
              <span class="info-label">Duration of Contract:</span>
              <span class="info-value">${formData.contractDuration}</span>
            </div>
            ` : ''}
            ${formData.preSolicitationListDate ? `
            <div class="info-row">
              <span class="info-label">Pre-Solicitation List Date:</span>
              <span class="info-value">${formData.preSolicitationListDate}</span>
            </div>
            ` : ''}
            ${formData.solicitationDate ? `
            <div class="info-row">
              <span class="info-label">Solicitation Date:</span>
              <span class="info-value">${formData.solicitationDate}</span>
            </div>
            ` : ''}
            ${formData.dueDate ? `
            <div class="info-row">
              <span class="info-label">Proposal Due Date:</span>
              <span class="info-value">${formData.dueDate}</span>
            </div>
            ` : ''}
            ${formData.awardDate ? `
            <div class="info-row">
              <span class="info-label">Estimated Award Date:</span>
              <span class="info-value">${formData.awardDate}</span>
            </div>
            ` : ''}
          </div>
          ` : ''}
        </div>
        
        ${formData.description ? `
        <div class="text-block">
          <div class="text-block-title">Description</div>
          <div class="text-block-content">${formData.description}</div>
        </div>
        ` : ''}
        
        ${formData.justification ? `
        <div class="text-block">
          <div class="text-block-title">Justification & Context</div>
          <div class="text-block-content">${formData.justification}</div>
        </div>
        ` : ''}

        ${formData.contractVehicle ? `
        <div class="text-block">
          <div class="text-block-title">Contract Vehicle</div>
          <div class="text-block-content">${formData.contractVehicle}</div>
        </div>
        ` : ''}
        
        ${(formData.pocs || []).filter(poc => poc.name || poc.title || poc.phone || poc.email).length > 0 ? `
        <div class="text-block">
          <div class="text-block-title">Points of Contact (POCs)</div>
          <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
            <thead>
              <tr style="background-color: #f8f9fa; text-align: left; border-bottom: 2px solid #dee2e6;">
                <th style="padding: 8px; font-weight: 700; color: #7f8c8d; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px;">Name</th>
                <th style="padding: 8px; font-weight: 700; color: #7f8c8d; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px;">Title</th>
                <th style="padding: 8px; font-weight: 700; color: #7f8c8d; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px;">Phone</th>
                <th style="padding: 8px; font-weight: 700; color: #7f8c8d; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px;">Email</th>
              </tr>
            </thead>
            <tbody>
              ${(formData.pocs || []).filter(poc => poc.name || poc.title || poc.phone || poc.email).map(poc => `
                <tr style="border-bottom: 1px solid #dee2e6;">
                  <td style="padding: 8px; font-weight: 600; color: #2c3e50;">${poc.name || 'N/A'}</td>
                  <td style="padding: 8px; color: #2c3e50;">${poc.title || 'N/A'}</td>
                  <td style="padding: 8px; color: #2c3e50;">${poc.phone || 'N/A'}</td>
                  <td style="padding: 8px; color: #2c3e50;"><a href="mailto:${poc.email}" style="color: #3498db; text-decoration: none;">${poc.email || 'N/A'}</a></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
        
        <div class="signature-section">
          <div class="signature-title">Review & Sign-Off</div>
          <div style="font-size: 14px; margin-bottom: 20px; color: #2c3e50;">
            <strong>Decision:</strong> [ &nbsp; ] Approved &nbsp; &nbsp; &nbsp; &nbsp; [ &nbsp; ] Disapproved
          </div>
          <div class="signature-grid">
            <div class="signature-box">
              Manager Signature
            </div>
            <div class="signature-box">
              Date
            </div>
          </div>
        </div>
        
      </div>
    `;

    openDocumentPrintWindow({
      title: `Opportunity Summary - ${formData.name || "N/A"}`,
      headStyles: styles,
      bodyHtml
    });
}
